import React from "react";
import queryString from "query-string";
import { cx } from "emotion";
import isHotkey from "is-hotkey";
import { TopicUserAutoCompleteItem } from "../Models/TopicUserAutoCompleteItem";
import { Topic } from "../Models/Topic";
import { endpoints } from "../Constants/endpoints";
import { ApiRequest } from "../../Common";
import ResponseHandler from "../../App/ResponseHandler";
import { ResponseResult } from "../../Common/Models/ResponseResult";
import "../Styles/TopicUsersAutocomplete.scss";
import { TopicUsersAutocompleteType } from "../Enums/TopicUsersAutocompleteType";

type Props = {
  topic: Topic;
  placeHolder?: string;
  ignoreExistingUsers: boolean;
  ignoreCurrentUser: boolean;
  autocompleteFor: TopicUsersAutocompleteType;
  onItemSelect: (selection: TopicUserAutoCompleteItem) => void;
};

type State = {
  items: TopicUserAutoCompleteItem[];
  value: string;
  showItems: boolean;
  highlightedIndex: number;
  isLoadingItems: boolean;
  allowBlur: boolean;
};

const COMPONENT_NAME = "TopicUsersAutocomplete";
const CLASSNAME_PREFIX = `${COMPONENT_NAME}__`;

const selectUpHotkey = isHotkey("up");
const selectDownHotkey = isHotkey("down");
const selectSuggestionHotkey = isHotkey("enter");

export class TopicUsersAutocomplete extends React.Component<Props, State> {
  static defaultProps = {
    ignoreExistingUsers: false,
    ignoreCurrentUser: false
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      items: [],
      value: "",
      showItems: false,
      highlightedIndex: -1,
      isLoadingItems: false,
      allowBlur: true
    };
  }

  componentDidMount = () => {
    window.addEventListener("keydown", this.handleKeyDown);
  };

  componentWillUnmount = () => {
    window.removeEventListener("keydown", this.handleKeyDown);
  };

  handleKeyDown = (event: KeyboardEvent) => {
    if (this.state.showItems) {
      if (selectUpHotkey(event)) {
        this.setState((prevState: State) => {
          let highlightedIndex = 0;
          if (prevState.highlightedIndex >= 0 && prevState.highlightedIndex - 1 > 0) {
            highlightedIndex = prevState.highlightedIndex - 1;
          }
          return {
            ...prevState,
            highlightedIndex
          };
        });
      }

      if (selectDownHotkey(event)) {
        this.setState((prevState: State) => {
          let highlightedIndex = 0;
          if (prevState.highlightedIndex >= 0) {
            if (prevState.highlightedIndex + 1 >= prevState.items.length) {
              highlightedIndex = prevState.items.length - 1;
            } else {
              highlightedIndex = prevState.highlightedIndex + 1;
            }
          }
          return {
            ...prevState,
            highlightedIndex
          };
        });
      }

      if (
        selectSuggestionHotkey(event) &&
        this.state.highlightedIndex >= 0 &&
        this.state.highlightedIndex < this.state.items.length
      ) {
        this.onItemSelect(this.state.items[this.state.highlightedIndex]);
      }
    }
  };

  getItems = (value: string) => {
    if (value.length > 0) {
      const queryArgs = {
        query: value,
        ignoreExisting: this.props.ignoreExistingUsers,
        ignoreSelf: this.props.ignoreCurrentUser,
        type: this.props.autocompleteFor,
        topicCode: this.props.topic.code
      };

      ApiRequest.fromPath(
        `${endpoints.search.searchTopicUsers()}?${queryString.stringify(queryArgs)}`
      )
        .withAuthHeader()
        .get()
        .then(response => {
          ResponseHandler.handle(
            response,
            (result: ResponseResult<TopicUserAutoCompleteItem[]>) => {
              this.setState({
                items: result.payload
              });
            },
            () => {},
            () => {}
          );
        });
    }
  };

  renderItem = (item: TopicUserAutoCompleteItem, isHighlighted: boolean, index: number) => {
    return (
      <div
        key={item.username}
        className={cx(
          `${CLASSNAME_PREFIX}item`,
          isHighlighted ? `${CLASSNAME_PREFIX}item-active` : undefined
        )}
        onClick={e => this.onItemClick(e, item)}
        onMouseEnter={e => this.onItemEnter(e, index)}
        onMouseLeave={e => this.onItemLeave(e, index)}
        onMouseMove={e => this.onItemMouseMove(e, index)}
      >
        {item.name}
      </div>
    );
  };

  getItemValue = (item: TopicUserAutoCompleteItem) => {
    return item.name;
  };

  onItemSelect = (item: TopicUserAutoCompleteItem) => {
    this.setState(
      {
        value: "",
        items: []
      },
      () => this.props.onItemSelect(item)
    );
  };

  onItemClick = (event: React.MouseEvent<HTMLDivElement>, item: TopicUserAutoCompleteItem) => {
    this.onItemSelect(item);
  };

  onItemEnter = (event: React.MouseEvent<HTMLDivElement>, index: number) => {
    this.setState({
      highlightedIndex: index
    });
  };

  onItemLeave = (event: React.MouseEvent<HTMLDivElement>, index: number) => {
    this.setState({
      highlightedIndex: -1
    });
  };

  onItemMouseMove = (event: React.MouseEvent<HTMLDivElement>, index: number) => {
    this.setState({
      highlightedIndex: index
    });
  };

  onInputFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    this.setState({ showItems: true });
    this.getItems(this.state.value);
  };

  onInputBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (this.state.allowBlur) {
      event.currentTarget.blur();
      this.setState({ showItems: false });
    } else {
      event.currentTarget.focus();
    }
  };

  onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode === 38 || event.keyCode === 40) {
      event.preventDefault();
    }
  };

  onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;

    this.setState({
      value,
      highlightedIndex: -1
    });

    if (/\S/.test(value)) {
      this.getItems(value);
    } else {
      this.setState({
        items: []
      });
    }
  };

  onItemsWrapperMouseDown = (event: React.MouseEvent) => {
    this.setState({
      allowBlur: false
    });
  };

  onItemsWrapperMouseUp = (event: React.MouseEvent) => {
    this.setState({
      allowBlur: true
    });
  };

  render() {
    return (
      <div className={COMPONENT_NAME}>
        <input
          className={cx(
            `${CLASSNAME_PREFIX}input`,
            this.state.items.length > 0 && this.state.showItems
              ? `${CLASSNAME_PREFIX}items-open`
              : undefined
          )}
          type="text"
          value={this.state.value}
          onChange={this.onInputChange}
          placeholder={this.props.placeHolder}
          onKeyDown={this.onInputKeyDown}
          onFocus={this.onInputFocus}
          onBlur={this.onInputBlur}
        />

        {this.state.showItems && this.state.items.length > 0 ? (
          <div
            className={`${CLASSNAME_PREFIX}items-wrapper`}
            onMouseDown={this.onItemsWrapperMouseDown}
            onMouseUp={this.onItemsWrapperMouseUp}
          >
            {this.state.items.map((suggestion, index) =>
              this.renderItem(suggestion, this.state.highlightedIndex === index, index)
            )}
          </div>
        ) : null}
      </div>
    );
  }
}
